import { Division } from "../features/api-interfaces";

// 共通ラベル
export const COMMON_LABELS = {
  NO: "No",
  /**Cloudscape固定文言 */
  CSD: {
    LOADING_TEXT: "Loading resources",
    NO_DATA: "表示するデータがありません",
    EMPTY_OPTION: "選択肢無し",
    USE: "検索：　",
  },
  BTNS: {
    DL: "ダウンロード",
    UL: "アップロード",
    ACTIVATE: "有効化",
    DEACTIVATE: "無効化",
    DELETE: "削除",
    CREATE: "　新規登録　",
    SEARCH: "　　検索　　",
    BACK: "　　戻る　　",
    REGISTER: "　　登録　　",
    UPDATE: "　　更新　　",
    ADD_USER_ROLE: "　権限追加　",
    CLEAR: "　クリア　",
    REFERENCE: "　　参照　　",
    VERIFY: "　　検証　　",
    APPLY: "　　申請　　",
    TEMPORARILY_SAVE: "　一時保存　",
    SEARCH_CORP_NUM: "法人番号検索",
    SEARCH_POSTAL_CODE: "郵便番号検索",
    DELETE_TEMPORARILY_SAVE: "一時保存削除",
    APPROVAL_REQUEST: "　承認依頼　",
    APPROVAL: "　　承認　　",
    SEND_BACK: "　　差戻　　",
    PULL_BACK: "　　引戻　　",
    DELETE_ROW: "　行削除　",
    ADD: "　　追加　　",
    NEXT: "　　次へ　　",
    EDIT: "　　編集　　",
    JUDGE: "　　判定　　",
    CONFIRMATION: "　　確認　　",
    SWITCH: "　　切替　　",
    CLOSE: "　閉じる　",
  },
  CONTENTS: {
    DL: "ダウンロードを開始します。よろしいでしょうか？",
    DL_WAIT:
      "ダウンロードを行います。処理に時間がかかる場合がありますが、よろしいですか？",
    DL_RUN: "ダウンロードしています",
    TEMPORARILY_SAVE: "画面の内容で一時保存します。",
    APPROVAL_REQUEST: "画面の内容で承認依頼します。",
    DELETE_TEMPORARILY_SAVE: "一時保存したデータを削除します。",
    APPROVAL: "画面の内容で承認します。",
    PULL_BACK: "承認依頼を引戻します。",
    APPROVED_PULL_BACK: "承認済を引戻します。",
    SEND_BACK: "画面の内容を差戻します。",
    REGISTER_CONFIRM: "画面の内容で登録します。",
    UPDATE_CONFIRM: "画面の内容で更新します。",
    DELETE_CONFIRM: "画面の内容を削除します。",
    APPLICATION_CONFIRM: "以下の内容で申請します。",
    PAGE_MOVE_CONFIRM: "この画面から移動します。",
    APPLICATION: "画面の内容で申請します。",
    JUDGEMENT: "画面の内容で判定します。",
    SWITCH_CONFIRM: "以下の内容で切替を実行します。",
    MAINTENANCE_NOTICE:
      "特殊対応（メンテナンス）用の申請書をアップロードしようとしています。続行してよろしいですか。",
    COMPANY_LINKED_CPID: "※サイトIDに紐づく事業者情報",
  },
  TITLES: {
    DL: "確認",
    UPDATE: "以下の内容で更新します。",
    REGISTER_STORE_INFO: "以下の内容で申請します。",
    REGISTER_CONFIRM: "登録してよろしいでしょうか？",
    UPDATE_CONFIRM: "更新してよろしいでしょうか？",
    DELETE_CONFIRM: "削除してよろしいでしょうか？",
  },
  POSITIVE_BUTTON: {
    OK: "　　OK　　",
    DECIDE: "決定",
  },
  NEGATIVE_BUTTON: {
    CANCEL: "キャンセル",
  },
  INPUT_ITEM_MESSAGE: {
    REQUIRED: "必須",
  },
  OTHER: {
    CPM: "CPM",
    MPM: "MPM",
  },
  BLANK: "",
  HYPHEN: "ー",
  LEFT_PARENTHESIS: "（",
  RIGHT_PARENTHESIS: "）",
  COLON: "：",
  CHAR_TYPE: {
    HALF_NUMBER: "半角数字",
  },
} as const;

export const COMMON_CONFIGS = {
  API: {
    STATUS_SUCCESS: "success",
    STATUS_ERROR: "error",
  },
} as const;

// ステータスインターフェース
interface StatusObj {
  statusCode: number;
  statusName: string;
}

export interface ApprovalStatus {
  beforeApproval: StatusObj;
  awaitingApproval: StatusObj;
  afterApproval: StatusObj;
  commercialApplication: StatusObj;
}

export const ApprovalStatusPattern: ApprovalStatus = {
  beforeApproval: {
    statusCode: 1,
    statusName: "承認前",
  },
  awaitingApproval: {
    statusCode: 2,
    statusName: "承認待",
  },
  afterApproval: {
    statusCode: 3,
    statusName: "承認済",
  },
  commercialApplication: {
    statusCode: 4,
    statusName: "商用反映済",
  },
};

// 画面表示ステータス
export const getStatus = (statusNumber: number): string => {
  switch (statusNumber) {
    case ApprovalStatusPattern.beforeApproval.statusCode:
      return ApprovalStatusPattern.beforeApproval.statusName;
    case ApprovalStatusPattern.awaitingApproval.statusCode:
      return ApprovalStatusPattern.awaitingApproval.statusName;
    case ApprovalStatusPattern.afterApproval.statusCode:
      return ApprovalStatusPattern.afterApproval.statusName;
    case ApprovalStatusPattern.commercialApplication.statusCode:
      return ApprovalStatusPattern.commercialApplication.statusName;
    default:
      // 想定外の数値の場合は空文字を設定する。
      return "";
  }
};

export interface ActionStatus {
  temporarilySave: StatusObj;
  pullBack: StatusObj;
  approvedPullBack: StatusObj;
  approvalRequest: StatusObj;
  sendBack: StatusObj;
  approval: StatusObj;
  delete: StatusObj;
}

export const ActionPattern: ActionStatus = {
  temporarilySave: {
    statusCode: 1,
    statusName: "一時保存",
  },
  pullBack: {
    statusCode: 2,
    statusName: "引戻（承認前）",
  },
  approvedPullBack: {
    statusCode: 3,
    statusName: "引戻（承認済）",
  },
  approvalRequest: {
    statusCode: 4,
    statusName: "承認依頼",
  },
  sendBack: {
    statusCode: 5,
    statusName: "差戻",
  },
  approval: {
    statusCode: 6,
    statusName: "承認",
  },
  delete: {
    statusCode: 7,
    statusName: "削除",
  },
};

export interface DivisionDict {
  label: string;
  value: string;
}

export const BLANK_LABEL_VALUE: DivisionDict = { label: "　", value: "" };

export interface DivisionDictDescription {
  label: string;
  value: string;
  description: string;
}
export const SELECT_BLANK_DESCRIPTION = {
  label: "　",
  value: "",
  description: "　",
};

// 加盟店種別番号
interface MerchantType {
  yagoFC: { label: string; value: number };
  yago: { label: string; value: number };
  commercialFacility: { label: string; value: number };
}
export const merchantTypePattern: MerchantType = {
  yago: {
    label: "屋号",
    value: 1,
  },
  yagoFC: {
    label: "屋号（FC）",
    value: 2,
  },
  commercialFacility: {
    label: "商業施設",
    value: 3,
  },
};

interface ShopReceiveUnitMethod {
  difference: DivisionDict;
  all: DivisionDict;
}

export const shopReceiveUnitMethodPattern: ShopReceiveUnitMethod = {
  difference: {
    label: "差分更新",
    value: "1",
  },
  all: {
    label: "全量更新",
    value: "2",
  },
};

// TODO 実装側が各コードリストの要素を取得しやすいように修正する
type codeMaster = {
  [key: string]: DivisionDict[];
};
export const CODE_MASTER: codeMaster = {
  BANK_ACCOUNT_TYPE: [
    {
      label: "普通",
      value: "1",
    },
    {
      label: "当座",
      value: "2",
    },
  ],
  PAYMENT_CYCLE_CODE: [
    {
      label: "毎月1回　月末締翌月25日頃支払",
      value: "2",
    },
    {
      label: "毎月2回　①15日締当月末支払　②月末締翌月15日頃支払",
      value: "3",
    },
    {
      label: "毎営業日",
      value: "6",
    },
  ],
  CONFIRMATION_STATUS: [
    { label: "確認中", value: "確認中" },
    { label: "確認済み", value: "確認済み" },
  ],
  CONFIRMATION_RESULT: [
    { label: "OK", value: "OK" },
    { label: "NG", value: "NG" },
  ],
};

//空行の選択肢を追加
export const addBlank = (list: DivisionDict[]) => {
  return [BLANK_LABEL_VALUE, ...(list || [])];
};
export const addBlankDescription = (list: DivisionDictDescription[]) => {
  if (list) list.unshift(SELECT_BLANK_DESCRIPTION as DivisionDictDescription);
  return list;
};

// 区分取得結果変換
export const getDivisionItems = (
  divisions: Division[] | undefined
): DivisionDict[] => {
  if (divisions !== undefined) {
    return divisions.map((division) => ({
      label: division.label,
      value: division.kbnCode,
    }));
  }
  return [];
};

// 区分連想配列取得
export const getDivisionDict = (
  divisions: DivisionDict[],
  value: string
): DivisionDict => {
  let division = { label: "", value: "" };
  divisions.forEach((item) => {
    if (item.value === value) {
      division = item;
    }
  });
  return division;
};

// 区分連想配列取得（ひとつめを自動的に取得）
export const getFirstDivisionDict = (
  divisions: DivisionDict[]
): DivisionDict => {
  if (divisions.length === 0) {
    return { label: "", value: "" };
  } else {
    return divisions[0];
  }
};

// 区分ラベル取得
export const getDivisionLabel = (Items: DivisionDict[], code: string) => {
  return getDivisionDict(Items, code).label;
};

// string -> boolean変換
export const stringToBoolean = (value: string | null | undefined): boolean => {
  // null、undefined は false
  if (!value) {
    return false;
  }
  // 小文字にして判定
  return value.toLowerCase() === "true" || value === "1";
};
